/**
 * Custom graphics controller for /pages/alpha.
 */
(() => {
  const runtime = window.CovermaticRuntime || window.AlphaRuntime || {};
  window.CovermaticRuntime = runtime;
  window.AlphaRuntime = runtime;

  const { Config, State, Utils, ApiClient, Events, FileUploader } = runtime;

  const PROMO_TEMPLATE_ONLY_RULE = 'Use the selected promo template thumbnail as a structural image reference for layout, placement, spacing, hierarchy, crop, and promo format. Treat it as a blank scaffold to build on, not as finished artwork. The dashed/dotted artwork box, placeholder icon, YOUR ARTWORK HERE text, guide borders, and any scaffold construction marks are only layout guides; remove them completely from the final image. Preserve only the intended placement, spacing, and hierarchy while replacing placeholder areas with the uploaded or selected artist assets and the artist creative direction. If an uploaded asset is square album cover artwork, keep that album art intact, exactly the same, fully visible, uncropped, undistorted, and unredrawn inside the promo graphic; do not extend, repaint, recompose, alter text, crop edges, cover faces, or change the cover design. Build around the original cover as a placed source asset. Automatically theme the full promo graphic from the uploaded album art: sample its dominant and accent colors, contrast level, lighting, texture, genre/mood cues, illustration or photo style, and typography energy, then extend those cues into the background, supporting shapes, text treatment, and platform-ready campaign styling without altering the album cover itself. If the artist provides little or no written direction, make confident design decisions from the album art and selected promo type. Do not copy placeholder text literally unless it matches the selected promo type; create an original finished promo graphic with artist assets as the main visual identity.';
  const PROMO_SYSTEM_PROMPTS = {
    out_now_graphic: `${PROMO_TEMPLATE_ONLY_RULE} Build a release-day OUT NOW promo graphic with premium Covermatic-style hierarchy, bold readable typography, strong contrast, genre-aware mood, and platform-ready polish.`,
    pre_save_graphic: `${PROMO_TEMPLATE_ONLY_RULE} Build a pre-save promo graphic with anticipation, date/CTA-ready hierarchy, clean readable typography, strong focal asset placement, and premium Covermatic campaign polish.`,
    coming_soon_graphic: `${PROMO_TEMPLATE_ONLY_RULE} Build a COMING SOON teaser promo graphic with mystery, anticipation, date-ready spacing, bold readable typography, and premium Covermatic campaign polish.`,
    new_single_graphic: `${PROMO_TEMPLATE_ONLY_RULE} Build a NEW SINGLE promo graphic with clean song-release hierarchy, streaming-ready messaging, bold readable typography, and premium Covermatic campaign polish.`,
    pre_order_graphic: `${PROMO_TEMPLATE_ONLY_RULE} Build a PRE-ORDER promo graphic with purchase-ready CTA hierarchy, release-date space, bold readable typography, and premium Covermatic campaign polish.`,
    listen_now_graphic: `${PROMO_TEMPLATE_ONLY_RULE} Build a LISTEN NOW promo graphic with immediate streaming CTA hierarchy, strong platform availability cues, bold readable typography, and premium Covermatic campaign polish.`,
    watch_now_graphic: `${PROMO_TEMPLATE_ONLY_RULE} Build a WATCH NOW promo graphic with cinematic video CTA hierarchy, thumbnail-grade readability, dramatic framing, and premium Covermatic campaign polish.`,
    social_banner: `${PROMO_TEMPLATE_ONLY_RULE} Build a release countdown promo graphic with urgent rollout energy, clear countdown hierarchy, story/social readability, strong contrast, and professional Covermatic polish.`,
    youtube_banner: `${PROMO_TEMPLATE_ONLY_RULE} Build a music video or visualizer promo graphic with cinematic watch-now energy, thumbnail-grade readability, dramatic framing, bold type hierarchy, and premium Covermatic visual direction.`,
    merch: `${PROMO_TEMPLATE_ONLY_RULE} Build a merch drop promo graphic with retail/product hierarchy, streetwear-grade campaign energy, clear shop/drop messaging, texture, contrast, and Covermatic polish.`,
    custom: `${PROMO_TEMPLATE_ONLY_RULE} Build an original promo graphic for an artist music release campaign using the selected promo type, uploaded assets, and optional written direction with bold hierarchy, intentional typography, strong contrast, clean composition, genre-aware mood, and platform-ready polish.`
  };
  class CreativeLabController {
    constructor() {
      this.modal = Utils.$('creativeLabAssetsModal');
      this.grid = Utils.$('creativeLabAssetsGrid');
      this.empty = Utils.$('creativeLabAssetsEmpty');
      this.loading = Utils.$('creativeLabAssetsLoading');
      this.loadMoreBtn = Utils.$('creativeLabAssetsLoadMore');
      this.sentinel = Utils.$('creativeLabAssetsSentinel');
      this.assetsObserver = null;
      this.count = Utils.$('creativeLabAssetsCount');
      this.doneBtn = Utils.$('creativeLabAssetsDone');
      this.selectedWrap = Utils.$('creativeLabSelectedAssets');
      this.selectedGrid = Utils.$('creativeLabSelectedAssetsGrid');
      this.generateBtn = Utils.$('creativeLabGenerate');
      this.generateLoading = Utils.$('creativeLabLoading');
      this.error = Utils.$('creativeLabError');
      this.type = Utils.$('creativeLabType');
      this.size = Utils.$('creativeLabSize');
      this.brief = Utils.$('creativeLabBrief');
      this.title = Utils.$('#creative-lab-title');
      this.subtitle = Utils.$('#creative-lab-subtitle');
      this.promoSelection = Utils.$('#creative-lab-promo-selection');
      this.promoImage = Utils.$('#creative-lab-promo-image');
      this.promoTitle = Utils.$('#creative-lab-promo-title');
      this.promoCopy = Utils.$('#creative-lab-promo-copy');
      this.changePromoBtn = Utils.$('#creative-lab-change-promo');
      this.selectedPromoDirection = null;
      this.offset = 0;
      this.hasMore = true;
      this.isLoading = false;
      this.assetRecords = new Map();
      this.selected = new Map();
      this.uploadedSelection = new Map();
      this.skipNextGridClick = false;

      this.uploader = new FileUploader('creativeLabUploadZone', 'creativeLabUploadInput', 'creativeLabUploadPreview');
      this.init();
    }

    init() {
      this.seedSelectedFromState();
      this.renderSelectedAssets();
      this.updateGenerateState();

      Utils.$('creativeLabOpenAssets')?.addEventListener('click', () => this.openModal());
      this.loadMoreBtn?.addEventListener('click', () => this.fetchNextPage());
      this.doneBtn?.addEventListener('click', () => this.commitSelection());
      Utils.$('creativeLabClearAssets')?.addEventListener('click', () => this.clearAssets());
      this.generateBtn?.addEventListener('click', () => this.submit());
      this.changePromoBtn?.addEventListener('click', () => runtime.getService?.('modals')?.openPromoGraphicsModal?.());

      [this.type, this.size, this.brief].forEach((el) => {
        el?.addEventListener('input', () => this.updateGenerateState());
        el?.addEventListener('change', () => this.updateGenerateState());
      });

      const handleCardSelection = (event) => {
        const card = event.target.closest('[data-creative-lab-asset-url]');
        if (!card || !this.grid?.contains(card)) return;
        if (event.type === 'click' && this.skipNextGridClick) {
          this.skipNextGridClick = false;
          return;
        }
        if (event.type === 'pointerup') this.skipNextGridClick = true;
        event.preventDefault();
        this.toggleAsset(card.dataset.creativeLabAssetUrl);
      };
      this.grid?.addEventListener('click', handleCardSelection);
      this.grid?.addEventListener('pointerup', handleCardSelection);

      document.addEventListener('click', (event) => {
        const removeBtn = event.target.closest('[data-creative-lab-remove-url]');
        if (removeBtn) {
          event.preventDefault();
          this.removeSelected(removeBtn.dataset.creativeLabRemoveUrl);
        }
      });

      this.initLazyLoading();

      Events.on('state:activeUploads', () => this.updateGenerateState());
      Events.on('promo-graphics:direction-selected', ({ detail }) => this.applyPromoDirection(detail || {}));
    }

    initLazyLoading() {
      if (!this.sentinel || typeof IntersectionObserver !== 'function') return;
      const root = this.modal?.querySelector('.covermatic-modal-body') || null;
      this.assetsObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting || this.modal?.classList.contains('hidden')) return;
        this.fetchNextPage();
      }, { root, rootMargin: '320px 0px' });
      this.assetsObserver.observe(this.sentinel);
    }

    seedSelectedFromState() {
      this.selected.clear();
      (Array.isArray(State.creativeLabAssets) ? State.creativeLabAssets : []).forEach((asset) => {
        if (!asset?.url) return;
        this.selected.set(asset.url, asset);
      });
    }

    normalizeAsset(item) {
      if (!item?.url) return null;
      return {
        url: String(item.url).trim(),
        title: String(item.title || item.file_name || item.unique_file_name || 'Saved asset').trim(),
        fileName: String(item.file_name || item.unique_file_name || '').trim() || null,
        assetFamily: String(item.asset_family || '').trim() || null,
        assetType: String(item.asset_type || '').trim() || null,
        sizePreset: String(item.size_preset || '').trim() || null,
        source: item.source || 'saved',
      };
    }

    consumePendingPromoAsset() {
      const pending = this.normalizeAsset(State.pendingPromoGraphicAsset || null);
      State.pendingPromoGraphicAsset = null;
      if (!pending?.url) return;

      this.seedSelectedFromState();
      this.selected.set(pending.url, pending);
      State.creativeLabAssets = Array.from(this.selected.values());
      this.renderSelectedAssets();
      this.rerenderGridSelectionState();
      this.syncCount();
      this.updateGenerateState();
    }

    openModal() {
      if (!this.modal) return;
      this.seedSelectedFromState();
      this.uploadedSelection.clear();
      this.offset = 0;
      this.hasMore = true;
      this.assetRecords.clear();
      if (this.grid) this.grid.innerHTML = '';
      this.empty?.classList.add('hidden');
      this.sentinel?.classList.remove('hidden');
      this.loadMoreBtn?.classList.remove('hidden');
      this.modal.classList.remove('hidden');
      Utils.syncBodyLock();
      this.syncCount();
      this.fetchNextPage();
    }

    closeModal() {
      this.modal?.classList.add('hidden');
      Utils.syncBodyLock();
    }

    async fetchNextPage() {
      if (this.isLoading || !this.hasMore) return;

      this.isLoading = true;
      this.loading?.classList.remove('hidden');
      if (this.loadMoreBtn) this.loadMoreBtn.disabled = true;

      try {
        const payload = await ApiClient.fetchHistoricalGenerations(18, this.offset);
        const rows = Array.isArray(payload?.rows) ? payload.rows : [];
        rows.forEach((row) => {
          const asset = this.normalizeAsset({
            url: row.image_url || row.url,
            title: row.artist_name || row.song_name || row.file_name || 'Saved asset',
            file_name: row.file_name,
            unique_file_name: row.unique_file_name,
            asset_family: row.asset_family,
            asset_type: row.asset_type,
            size_preset: row.size_preset,
            source: 'saved',
          });
          if (!asset || this.assetRecords.has(asset.url)) return;
          this.assetRecords.set(asset.url, asset);
          this.grid?.insertAdjacentHTML('beforeend', this.renderAssetCard(asset));
        });
        this.offset += rows.length;
        this.hasMore = rows.length > 0 && (payload.total_rows ? this.offset < payload.total_rows : rows.length >= 18);
        this.empty?.classList.toggle('hidden', this.assetRecords.size > 0);
      } catch (error) {
        console.error('[CreativeLab] Failed to load saved assets', error);
        Utils.toast('Failed to load saved assets', 'error');
      } finally {
        this.isLoading = false;
        this.loading?.classList.add('hidden');
        this.sentinel?.classList.toggle('hidden', !this.hasMore);
        if (this.loadMoreBtn) {
          this.loadMoreBtn.disabled = !this.hasMore;
          this.loadMoreBtn.classList.toggle('hidden', !this.hasMore);
        }
      }
    }

    renderAssetCard(asset) {
      const active = this.selected.has(asset.url);
      return `
        <button type="button" aria-pressed="${active ? 'true' : 'false'}" class="tpl-card group text-left rounded-xl border ${active ? 'border-gray-900' : 'border-gray-200'} bg-white hover:shadow-lg transition duration-150 p-3 hover:border-brand-500" data-creative-lab-asset-url="${Utils.escapeHtml(asset.url)}">
          <div class="relative">
            <img src="${Utils.escapeHtml(asset.url)}" alt="${Utils.escapeHtml(asset.title || 'Selected asset')}" class="w-full aspect-square object-cover rounded-lg border group-hover:opacity-90 transition duration-150" loading="lazy" decoding="async" />
            <span class="absolute right-2 top-2 inline-flex h-6 w-6 items-center justify-center rounded-full ${active ? 'bg-gray-900 text-white' : 'bg-white/90 text-gray-700'} text-xs font-semibold">${active ? '&#10003;' : '+'}</span>
          </div>
        </button>
      `;
    }

    rerenderGridSelectionState() {
      Utils.$$('[data-creative-lab-asset-url]', this.grid).forEach((card) => {
        const active = this.selected.has(card.dataset.creativeLabAssetUrl);
        card.setAttribute('aria-pressed', active ? 'true' : 'false');
        card.classList.toggle('border-gray-900', active);
        card.classList.toggle('border-gray-200', !active);
        const badge = card.querySelector('span');
        if (badge) {
          badge.className = `absolute right-2 top-2 inline-flex h-6 w-6 items-center justify-center rounded-full ${active ? 'bg-gray-900 text-white' : 'bg-white/90 text-gray-700'} text-xs font-semibold`;
          badge.innerHTML = active ? '&#10003;' : '+';
        }
      });
    }

    toggleAsset(url) {
      if (!url) return;
      if (this.selected.has(url)) {
        this.selected.delete(url);
      } else {
        const asset = this.assetRecords.get(url) || this.uploadedSelection.get(url);
        if (asset) this.selected.set(url, asset);
      }
      this.syncSelectionState();
      this.rerenderGridSelectionState();
      this.syncCount();
    }

    syncSelectionState() {
      State.creativeLabAssets = Array.from(this.selected.values());
      this.renderSelectedAssets();
      this.updateGenerateState();
    }

    commitSelection() {
      this.uploader?.uploadedUrls?.forEach((url, index) => {
        if (!url) return;
        const uploadedAsset = {
          url,
          title: `Upload ${index + 1}`,
          fileName: null,
          source: 'upload',
          assetFamily: null,
          assetType: null,
          sizePreset: null,
        };
        this.uploadedSelection.set(url, uploadedAsset);
        this.selected.set(url, uploadedAsset);
      });

      this.syncSelectionState();
      this.closeModal();
    }

    clearAssets() {
      this.selected.clear();
      this.uploadedSelection.clear();
      this.uploader?.reset();
      this.syncSelectionState();
      this.syncCount();
      this.updateGenerateState();
    }

    removeSelected(url) {
      if (!url) return;
      this.selected.delete(url);
      this.uploadedSelection.delete(url);
      this.syncSelectionState();
      this.rerenderGridSelectionState();
      this.syncCount();
      this.updateGenerateState();
    }

    normalizeReferenceUrl(url) {
      const value = String(url || '').trim();
      if (!value) return '';
      if (value.startsWith('//')) return `https:${value}`;
      try {
        return new URL(value, window.location.origin).toString();
      } catch (error) {
        return value;
      }
    }
    applyPromoDirection(direction) {
      const normalized = {
        type: direction.type || 'custom',
        label: direction.label || 'Promo Graphic',
        size: direction.size || 'square_1_1',
        image: this.normalizeReferenceUrl(direction.image || ''),
        brief: direction.brief || '',
        copy: direction.copy || '',
        systemPrompt: direction.systemPrompt || PROMO_SYSTEM_PROMPTS[direction.type] || PROMO_SYSTEM_PROMPTS.custom,
      };
      this.selectedPromoDirection = normalized;
      State.entryChoice = 'promo-graphics';
      State.creativeLabPromoDirection = normalized;
      this.consumePendingPromoAsset();

      if (this.type) this.type.value = normalized.type;
      if (this.size) this.size.value = normalized.size;
      if (this.brief) this.brief.value = '';

      if (this.title) this.title.textContent = normalized.label;
      if (this.subtitle) this.subtitle.textContent = 'Create release-ready promo graphics for your single, EP, or album using your artwork, branding, and creative direction.';
      if (this.promoTitle) this.promoTitle.textContent = normalized.label;
      if (this.promoCopy) this.promoCopy.textContent = normalized.copy || 'Generate launch-day visuals announcing that your music is officially available on streaming platforms.';
      if (this.promoImage) {
        this.promoImage.src = normalized.image;
        this.promoImage.alt = `${normalized.label} selected direction`;
      }
      this.promoSelection?.classList.remove('hidden');

      runtime.getService?.('flow')?.showCreativeLabPanel?.();
      this.updateGenerateState();
      window.setTimeout(() => Utils.$('creativeLabPanel')?.scrollIntoView({ block: 'start' }), 0);
    }
    renderSelectedAssets() {
      const assets = Array.isArray(State.creativeLabAssets) ? State.creativeLabAssets : [];
      if (!this.selectedWrap || !this.selectedGrid) return;
      if (!assets.length) {
        this.selectedWrap.classList.add('hidden');
        this.selectedGrid.innerHTML = '';
        return;
      }

      this.selectedWrap.classList.remove('hidden');
      this.selectedGrid.innerHTML = assets.map((asset) => `
        <div class="relative group">
          <img src="${Utils.escapeHtml(asset.url)}" alt="${Utils.escapeHtml(asset.title || 'Selected asset')}" class="w-full aspect-square object-cover rounded-lg border border-gray-200 bg-white" loading="lazy" decoding="async" />
          <button type="button" class="absolute top-2 right-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-black/80 text-white opacity-0 transition group-hover:opacity-100" data-creative-lab-remove-url="${Utils.escapeHtml(asset.url)}" aria-label="Remove selected asset">&times;</button>
        </div>
      `).join('');
    }

    syncCount() {
      if (!this.count) return;
      this.count.textContent = `${this.selected.size} asset${this.selected.size === 1 ? '' : 's'} selected`;
    }

    showError(message) {
      if (!this.error) return;
      if (!message) {
        this.error.classList.add('hidden');
        return;
      }
      this.error.textContent = message;
      this.error.classList.remove('hidden');
    }

    updateGenerateState() {
      if (!this.generateBtn) return;
      const uploadsInProgress = Number(State.activeUploads || 0) > 0;
      this.generateBtn.disabled = uploadsInProgress;
    }

    submit() {
      if (!Utils.isLoggedIn()) {
        Events.emit('modal:buy-open-requested');
        return;
      }

      if (State.credits < Config.creditsGen) {
        Events.emit('modal:buy-open-requested');
        return;
      }

      const assets = Array.isArray(State.creativeLabAssets) ? State.creativeLabAssets : [];

      if (Number(State.activeUploads || 0) > 0) {
        this.showError('Wait for uploads to finish before generating.');
        return;
      }

      const creativeDirection = String(this.brief?.value || '').trim();
      const activePromoDirection = this.selectedPromoDirection || State.creativeLabPromoDirection || null;
      if (!activePromoDirection && !creativeDirection) {
        this.showError('Add your creative direction before generating.');
        return;
      }

      const assetInputs = Array.from(new Set([
        this.normalizeReferenceUrl(activePromoDirection?.image),
        ...assets.map((asset) => asset.url),
      ].filter(Boolean)));

      if (activePromoDirection && !assetInputs.length) {
        this.showError('Choose or upload at least one asset for the promo graphic.');
        return;
      }

      this.showError(null);
      this.generateLoading?.classList.remove('hidden');

      State.entryChoice = activePromoDirection ? 'promo-graphics' : 'creative-lab';
      State.creativeLabPromoDirection = activePromoDirection;

      runtime.TaskManager?.createTask({
        type: 'creative-lab',
        concept: creativeDirection,
        assetType: this.type?.value || 'custom',
        assetTypeLabel: this.selectedPromoDirection?.label || this.type?.selectedOptions?.[0]?.textContent?.trim() || 'Custom Graphics',
        sizePreset: this.size?.value || 'square_1_1',
        assetInputs,
        assetFamily: 'cover_art',
        promoDirection: activePromoDirection,
        systemPrompt: activePromoDirection?.systemPrompt || PROMO_SYSTEM_PROMPTS[this.type?.value] || PROMO_SYSTEM_PROMPTS.custom,
      });

      runtime.getService?.('flow')?.showCreativeLabPanel?.();
      runtime.getService?.('form')?.save?.();
      Utils.toast(activePromoDirection ? 'Promo graphic queued' : 'Graphic request queued');
      this.generateLoading?.classList.add('hidden');
      window.setTimeout(() => Utils.$('creativeLabPanel')?.scrollIntoView({ block: 'start' }), 0);
    }
  }

  runtime.CreativeLabController = CreativeLabController;
})();
